import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "@/shared/i18n/LocaleProvider";
import type { CrashFallbackProps } from "@/shared/ui/ErrorBoundary";
import { HeaderButton } from "@/shared/ui/StepHeaderControls";
import { useTimeZone } from "@/shared/time/TimeZoneProvider";

export function RouteErrorFallback({ error, reset }: CrashFallbackProps) {
  const { t } = useTranslation();
  const timeZone = useTimeZone();
  const [where] = useState(() => ({
    path: window.location.pathname + window.location.search,
    at: new Date(),
  }));
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    console.error("[simon] render error caught by RouteErrorFallback:", error);
  }, [error]);

  function copyReport() {
    navigator.clipboard.writeText(crashReport(error, where)).then(
      () => setCopied(true),
      () => setCopied(false),
    );
  }

  return (
    <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-[var(--color-bg)] p-[24px]">
      <div
        role="alert"
        className="flex w-full max-w-[560px] flex-col gap-[12px] rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-paper)] px-[20px] py-[18px]"
      >
        <p className="font-(family-name:--font-display) text-md text-[var(--color-text-strong)]">
          {t("crash.route.heading")}
        </p>
        <div className="mt-[4px] flex flex-wrap items-center gap-[16px]">
          <HeaderButton primary onClick={() => reset()}>
            {t("crash.retry")}
          </HeaderButton>
          <Link
            to="/"
            className="font-(family-name:--font-display) text-sm text-[var(--color-brand)] underline-offset-2 hover:underline"
          >
            {t("crash.home")}
          </Link>
        </div>
        <details className="mt-[4px]">
          <summary className="cursor-pointer font-(family-name:--font-display) text-sm text-[var(--color-text-muted)] marker:text-[var(--color-text-faint)] hover:text-[var(--color-text-dim)]">
            {t("crash.details.toggle")}
          </summary>
          <div className="mt-[8px] flex items-start gap-[8px]">
            <pre className="min-w-0 flex-1 select-all whitespace-pre-wrap break-words rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-hover-faint)] px-[10px] py-[8px] font-mono text-xs leading-relaxed text-[var(--color-text-dim)]">
              {`${errorLine(error)}\n${where.path} · ${where.at.toLocaleTimeString("sv-SE", { timeZone })}`}
            </pre>
            <HeaderButton onClick={copyReport}>
              {t(copied ? "crash.details.copied" : "crash.details.copy")}
            </HeaderButton>
          </div>
        </details>
      </div>
    </div>
  );
}

function crashReport(error: Error, where: { path: string; at: Date }): string {
  return [errorLine(error), where.path, where.at.toISOString()].join("\n");
}

function errorLine(error: Error): string {
  return `${error.name || "Error"}: ${error.message || "(no message)"}`;
}
